import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Modal from '@mui/material/Modal';
import { Box, Tabs, Tab } from '@mui/material';
import CheckSheet from 'src/screen/Protected/DeviceScreen/TestingReport/CheckSheet';
import CommissioningSignOff from 'src/screen/Protected/DeviceScreen/Commissioning/CommissioningSignOff';
import Button from 'src/component/UI/Button';
import { getDeviceById } from 'src/module/device/selector';
import { Map, fromJS, List } from 'immutable';
import { getAbilities } from 'src/module/authentication/selector';
import { fetchCheckSheet, fetchCheckSheetTasks, saveCheckSheet, updateCheckSheet } from 'src/module/checkSheet/action';
import { getCheckSheetReport, getCheckSheetTasks, getCheckSheetTasksLoading, getSaveCheckSheetReportLoading } from 'src/module/checkSheet/selector';
import LogoSpinner from 'src/component/UI/LogoSpinner';
import useMediaQuery from '@mui/material/useMediaQuery';
import variables from 'src/style/variable/variables.module.scss';
import { useTranslation } from 'src/i18n';
import CircuitInfo from './CircuitInfo';
import CircuitDynamicTable from './CircuitDynamicTable';

const selectGeneralTasks = getCheckSheetTasks('commissioning-circuit-general');

export default function CommissioningCircuitReport (props) {
  const { deviceId, reportId, open } = props;

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState('1');
  const generalTasks = useSelector(selectGeneralTasks);
  const report = useSelector(getCheckSheetReport(reportId));
  const device = useSelector(getDeviceById(deviceId));
  const isSaveLoading = useSelector(getSaveCheckSheetReportLoading('commissioning-circuit'));

  const isLoadingTasks = useSelector(getCheckSheetTasksLoading);
  const isMobile = useMediaQuery(`(max-width: ${variables.mobileWidth})`);
  const abilities: any = useSelector(getAbilities);
  const isPhoneView = useMediaQuery(`(max-width: ${variables.phoneWidth})`);


  const canEdit = abilities.includes('edit-check-sheet-report');
  const readOnly = Boolean(reportId) && !canEdit;

  const buildEmptyResults = (tasks) => {
    const tasksList = tasks.toJS ? tasks.toJS() : [];
    return fromJS({
      info: {
        type: 'commissioning-circuit',
        deviceId: deviceId || null,
      },
      'commissioning-circuit-general': tasksList.map((task: any) => ({
        task_id: task.key,
        status: null,
        comment: '',
      })),
      extras: {
        projectInfo: {
          project: '', area: '', cableType: '', ehtTag: device?.get('tag') || '',
          numTraces: '', manufacturer: '', controlPanel: '', distributionPanel: '',
          controllerHtc: '', circuitBreaker: '', numJb: '', numRtd: '',
          ehtIso: '', isoRevision: '',
        },
        controllerConfig: {
          address: '', lowTempAlarm: '', controlMode: '', highCurrentAlarm: '',
          setpointTemp: '', lowCurrentAlarm: '', groundFaultAlarm: '', groundFaultTripSetpoint: '',
        },
        heatTraceResults: [{ zone: t('commissioningCircuit.heatTrace.allCircuits'), cablePartNumber: '', resistance: '', ir: '', voltageDesign: '', voltageTest: '', currentDesign: '', currentStartup: '', currentSteady: '' }],
        rtdTestResults: [],
        testInstruments: [],
        deficiencies: [],
        deficienciesNotes: '',
      },
      signOff: {
        recommendations: '',
        commissioningStatus: '',
      },
    }) as Map<string, any>;
  };

  const [allResults, setAllResults] = useState(Map({}));
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    if (!reportId && generalTasks && generalTasks.size > 0) {
      setAllResults(buildEmptyResults(generalTasks));
    }
  }, [generalTasks, reportId]);

  useEffect(() => {
    if (open && !reportId) {
      setTabValue('1');
      setSubmitAttempted(false);
      if (generalTasks && generalTasks.size > 0) {
        setAllResults(buildEmptyResults(generalTasks));
      }
    }
  }, [open]);

  useEffect(() => {
    dispatch(fetchCheckSheetTasks('commissioning-circuit-general'));
  }, []);

  useEffect(() => {
    if (open && reportId) {
      dispatch(fetchCheckSheet(reportId, 'commissioning-circuit-general'));
    }
  }, [open, reportId, dispatch]);

  useEffect(() => {
    if (reportId && report.get('checkSheet')) {
      setAllResults(Map(report));
    }
  }, [report, reportId]);

  const heatTraceColumns = useMemo(() => [
    { key: 'zone', label: t('commissioningCircuit.heatTrace.zone'), width: '20%' },
    { key: 'cablePartNumber', label: t('commissioningCircuit.heatTrace.cablePartNumber'), width: '12%' },
    { key: 'resistance', label: t('commissioningCircuit.heatTrace.resistance') },
    { key: 'ir', label: t('commissioningCircuit.heatTrace.ir') },
    { key: 'voltageDesign', label: t('commissioningCircuit.heatTrace.voltageDesign') },
    { key: 'voltageTest', label: t('commissioningCircuit.heatTrace.voltageTest') },
    { key: 'currentDesign', label: t('commissioningCircuit.heatTrace.currentDesign') },
    { key: 'currentStartup', label: t('commissioningCircuit.heatTrace.currentStartup') },
    { key: 'currentSteady', label: t('commissioningCircuit.heatTrace.currentSteady') },
  ], [t]);

  const rtdTestColumns = useMemo(() => [
    { key: 'rtdId', label: t('commissioningCircuit.rtd.id') },
    { key: 'rtdReading', label: t('commissioningCircuit.rtd.reading') },
    { key: 'failTest', label: t('commissioningCircuit.rtd.failTest') },
    { key: 'resistanceRW', label: t('commissioningCircuit.rtd.resistanceRW') },
    { key: 'resistanceRB', label: t('commissioningCircuit.rtd.resistanceRB') },
    { key: 'resistanceWB', label: t('commissioningCircuit.rtd.resistanceWB') },
    { key: 'comments', label: t('commissioningCircuit.rtd.comments') },
  ], [t]);

  const testInstrumentColumns = useMemo(() => [
    { key: 'manufacturer', label: t('commissioningCircuit.testInstruments.manufacturer') },
    { key: 'model', label: t('commissioningCircuit.testInstruments.model') },
    { key: 'range', label: t('commissioningCircuit.testInstruments.range') },
    { key: 'idNo', label: t('commissioningCircuit.testInstruments.idNo') },
    { key: 'calibrationDueDate', label: t('commissioningCircuit.testInstruments.calibrationDueDate'), type: 'date' },
  ], [t]);

  const deficienciesColumns = useMemo(() => [
    { key: 'item', label: t('commissioningCircuit.deficiencies.item') },
    { key: 'type', label: t('commissioningCircuit.deficiencies.type') },
    { key: 'notes', label: t('commissioningCircuit.deficiencies.notes') },
  ], [t]);

  if (!open) {
    return null;
  }

  if (reportId && !report.get('checkSheet')) {
    return null;
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const statusError = submitAttempted && !allResults.getIn(['signOff', 'commissioningStatus']);

  const next = () => setTabValue((parseInt(tabValue) + 1).toString());
  const previous = () => setTabValue((parseInt(tabValue) - 1).toString());

  const submit = () => {
    if (!allResults.getIn(['signOff', 'commissioningStatus'])) {
      setSubmitAttempted(true);
      setTabValue('6');
      return;
    }
    const values: any = allResults.toJS();
    const c = [...(values['commissioning-circuit-general'] || [])];
    // Use local state's deviceId so user-driven link/unlink/relink in the modal is preserved
    const effectiveDeviceId = allResults.getIn(['info', 'deviceId']) ?? deviceId ?? null;
    let payload = allResults
      .setIn(['info', 'type'], 'commissioning-circuit')
      .setIn(['info', 'deviceId'], effectiveDeviceId)
      .set('checkSheet', fromJS(c))
      .set('signOff', allResults.get('signOff'));
    if (reportId) {
      payload = payload.set('id', reportId);
      dispatch(updateCheckSheet(payload, effectiveDeviceId, 'commissioning-circuit'));
    } else {
      dispatch(saveCheckSheet('commissioning-circuit', payload, effectiveDeviceId));
    }
  };

  const change = (path, value) => {
    setAllResults(prev => prev.setIn(path, value));
  };

  const handleUnlinkDevice = () => {
    setAllResults(prev => prev.set('info', prev.get('info').set('deviceId', null)));
  };

  const handleDeviceSelect = (selectedDeviceId: string) => {
    setAllResults(prev => prev.set('info', prev.get('info').set('deviceId', selectedDeviceId)));
  };

  const circuitTag = device ? device.get('tag') : null;
  const parentTag = device ? device.get('parent_tag') : null;
  const deviceTitleTag = parentTag ? `${parentTag}-${circuitTag}` : circuitTag;
  const titleTag = deviceTitleTag || allResults?.getIn(['extras', 'projectInfo', 'ehtTag']);

  return (
    <Modal open={props.open} onClose={props.handleClose} style={{ height: '90vh' }}>
      <div className={`modal testing-report-modal commissioning-modal${isPhoneView ? ' modal-no-padding-sides' : ''}`}>
        {!isMobile && (
          <h2>{t('commissioningCircuit.modalTitle')} {titleTag && <b>({titleTag})</b>}</h2>
        )}
        {isLoadingTasks ? (
          <div style={{ margin: 'auto', marginTop: '200px', width: '100px' }}>
            <LogoSpinner size={100} />
          </div>
        ) : (
          <>
            <Box sx={{ width: '100%', marginBottom: '20px' }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                textColor='primary'
                indicatorColor='primary'
                aria-label="commissioning-circuit-tabs"
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab value="1" label={<span className='tab-label-size'>{t('commissioningCircuit.info')}</span>} />
                <Tab value="2" label={<span className='tab-label-size'>{t('commissioningCircuit.commissioningChecks')}</span>} />
                <Tab value="3" label={<span className='tab-label-size'>{t('commissioningCircuit.heatTraceResults')}</span>} />
                <Tab value="4" label={<span className='tab-label-size'>{t('commissioningCircuit.rtdTestResults')}</span>} />
                <Tab value="5" label={<span className='tab-label-size'>{t('commissioningCircuit.testInstrumentsAndDeficiencies')}</span>} />
                <Tab value="6" label={<span className='tab-label-size'>{t('commissioningCircuit.signOffAndComments')}</span>} />
              </Tabs>
            </Box>

            {tabValue === '1' && (
              <CircuitInfo
                readOnly={readOnly}
                data={allResults?.get('extras')}
                onChange={change}
                linkedDeviceTag={device?.get('tag')}
                onUnlink={handleUnlinkDevice}
                onDeviceSelect={handleDeviceSelect}
              />
            )}
            {tabValue === '2' && (
              <CheckSheet
                readOnly={readOnly}
                onChange={change}
                id='commissioning-circuit-general'
                title={t('commissioningCircuit.commissioningChecks')}
                data={allResults?.get('commissioning-circuit-general')}
                checkSheetType="commissioning-circuit-general"
              />
            )}
            {tabValue === '3' && (
              <CircuitDynamicTable
                title={t('commissioningCircuit.heatTraceResults')}
                columns={heatTraceColumns}
                data={allResults?.getIn(['extras', 'heatTraceResults']) as List<any>}
                extrasKey="heatTraceResults"
                onChange={change}
                readOnly={readOnly}
              />
            )}
            {tabValue === '4' && (
              <CircuitDynamicTable
                title={t('commissioningCircuit.rtdTestResults')}
                columns={rtdTestColumns}
                data={allResults?.getIn(['extras', 'rtdTestResults']) as List<any>}
                extrasKey="rtdTestResults"
                onChange={change}
                readOnly={readOnly}
              />
            )}
            {tabValue === '5' && (
              <>
                <CircuitDynamicTable
                  title={t('commissioningCircuit.testInstruments.title')}
                  columns={testInstrumentColumns}
                  data={allResults?.getIn(['extras', 'testInstruments']) as List<any>}
                  extrasKey="testInstruments"
                  onChange={change}
                  readOnly={readOnly}
                />
                <CircuitDynamicTable
                  title={t('commissioningCircuit.deficiencies.title')}
                  columns={deficienciesColumns}
                  data={allResults?.getIn(['extras', 'deficiencies']) as List<any>}
                  extrasKey="deficiencies"
                  onChange={change}
                  readOnly={readOnly}
                  typeLegend={t('commissioningCircuit.deficiencies.typeLegend')}
                />
              </>
            )}
            {tabValue === '6' && (
              <CommissioningSignOff
                readOnly={readOnly}
                data={allResults?.get('signOff')}
                onChange={change}
                statusError={statusError}
              />
            )}

            <div className='button-bar stay-at-bottom'>
              <Button onClick={() => props.handleClose()}>{t('common.cancel')}</Button>
              {tabValue !== '1' && <Button onClick={previous}>{t('common.previous')}</Button>}
              {tabValue === '6'
                ? <Button onClick={submit} disabled={readOnly} loading={isSaveLoading} cta>{reportId ? t('common.save') : t('common.submit')}</Button>
                : <Button className='next-button' onClick={next} cta>{t('common.next')}</Button>
              }
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

CommissioningCircuitReport.propTypes = {
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  deviceId: PropTypes.string,
  reportId: PropTypes.string,
};
